import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { kaufenContent } from '../data/pageContent';

const UnternehmenKaufenPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Helmet>
        <title>{kaufenContent.title}</title>
        <meta name="description" content={kaufenContent.description} />
        <link rel="canonical" href="https://www.unternehmenbewerten.ch/unternehmen-kaufen" />
      </Helmet>
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 lg:py-24 bg-gradient-to-b from-[#f8f9fa] to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <nav className="text-sm text-[#5a5d5b] mb-4">
                <Link to="/" className="hover:text-[#FF0000]">Home</Link>
                <span className="mx-2">/</span>
                <span>Unternehmen kaufen</span>
              </nav>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#1a1c1b] mb-6">
                {kaufenContent.h1}
              </h1>
              <p className="text-lg text-[#5a5d5b] mb-4">
                {kaufenContent.intro}
              </p>
              <p className="text-[#5a5d5b] mb-8">
                {kaufenContent.introExtended}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/bewertung" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors">
                  Unternehmenswert prüfen
                  <ArrowRight size={18} />
                </Link>
                <Link to="/kontakt" className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#1a1c1b] text-[#1a1c1b] font-semibold rounded-lg hover:bg-[#1a1c1b] hover:text-white transition-colors">
                  Kaufberatung anfragen
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Warum eine Firma kaufen? */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              Warum eine Firma kaufen?
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8 max-w-3xl mx-auto">
              {kaufenContent.advantagesIntro}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {kaufenContent.advantages.map((advantage) => (
                <div key={advantage.title} className="bg-[#f8f9fa] rounded-xl p-6 border border-gray-100">
                  <h3 className="text-[#FF0000] font-semibold mb-2">{advantage.title}</h3>
                  <p className="text-[#5a5d5b] text-sm">{advantage.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* So gehen wir vor */}
        <section className="py-16 bg-[#f8f9fa]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              So gehen wir vor
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8 max-w-3xl mx-auto">
              {kaufenContent.processIntro}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              {kaufenContent.processSteps.map((step) => (
                <div key={step.step} className="bg-white rounded-xl p-5 border border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-[#FF0000] text-white flex items-center justify-center font-bold mb-3">
                    {step.step}
                  </div>
                  {step.link ? (
                    <Link to={step.link} className="text-[#1a1c1b] font-semibold mb-2 block hover:text-[#FF0000] text-sm">{step.title}</Link>
                  ) : (
                    <h3 className="text-[#1a1c1b] font-semibold mb-2 text-sm">{step.title}</h3>
                  )}
                  <p className="text-[#5a5d5b] text-xs">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Worauf Sie beim Firmenkauf achten sollten */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              Worauf Sie beim Firmenkauf achten sollten
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8">
              {kaufenContent.dueDiligenceIntro}
            </p>
            <div className="bg-[#f8f9fa] rounded-2xl p-8 border border-gray-100">
              <ul className="space-y-4">
                {kaufenContent.dueDiligence.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#FF0000] flex-shrink-0 mt-0.5" />
                    <span className="text-[#1a1c1b]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Ihre ersten Schritte */}
        <section className="py-16 bg-[#f8f9fa]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              Ihre ersten Schritte zum Firmenkauf
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8">
              {kaufenContent.firstStepsIntro}
            </p>
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <ul className="space-y-4">
                {kaufenContent.firstSteps.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#FF0000] flex-shrink-0 mt-0.5" />
                    <span className="text-[#1a1c1b]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Warum unternehmenbewerten.ch */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-6 text-center">
              Warum unternehmenbewerten.ch als Kaufberater?
            </h2>
            <p className="text-[#5a5d5b] leading-relaxed mb-8">
              {kaufenContent.trustSection}
            </p>
            <p className="text-[#5a5d5b]">
              Lassen Sie den fairen Wert eines Kaufobjekts mit unserer{' '}
              <Link to="/unternehmensbewertung" className="text-[#FF0000] hover:underline">Unternehmensbewertung</Link> prüfen. Unsere{' '}
              <Link to="/ma-beratung" className="text-[#FF0000] hover:underline">M&amp;A-Berater</Link> begleiten Sie von der Due Diligence bis zum erfolgreichen Closing.
            </p>
            <div className="text-center mt-8">
              <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors">
                Kaufberatung anfragen
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        {/* Häufige Fragen */}
        <section className="py-16 bg-[#f8f9fa]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-8 text-center">
              Häufige Fragen zum Firmenkauf
            </h2>
            <div className="space-y-6">
              {kaufenContent.faq.map((item) => (
                <div key={item.question} className="bg-white rounded-xl p-6 border border-gray-100">
                  <h3 className="text-[#1a1c1b] font-semibold mb-3">{item.question}</h3>
                  <p className="text-[#5a5d5b] leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Referenzen Teaser */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-[#f8f9fa] rounded-2xl p-8 border border-gray-100 flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-[#1a1c1b] mb-2">So funktioniert ein Management-Buy-in</h3>
                <p className="text-[#5a5d5b]">Erfahren Sie anhand eines anonymisierten Praxisbeispiels, wie ein externer Käufer ein KMU übernimmt.</p>
              </div>
              <Link to="/referenzen#mbi-handelsunternehmen" className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors whitespace-nowrap">
                MBI-Beispiel ansehen
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#1a1c1b]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Bereit für Ihren Firmenkauf?
            </h2>
            <p className="text-gray-300 mb-8">
              Kontaktieren Sie uns für ein unverbindliches Erstgespräch.
            </p>
            <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors">
              Kaufberatung anfragen
              <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
      {/* Schemas werden ausschliesslich via SSG geliefert - keine Duplikate durch React */}
    </div>
  );
};

export default UnternehmenKaufenPage;
